import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api'
import { Loading } from '../Loading'

function pageTitle(kind: string, round: number | null) {
  if (kind === 'pairings' && round != null) return { title: `第 ${round} 輪 對陣`, sub: `Round ${round}` }
  if (kind === 'round_results' && round != null) return { title: `第 ${round} 輪 結果`, sub: `Round ${round} Result` }
  if (kind === 'player_list') return { title: '參賽名單', sub: '' }
  if (kind === 'standings') return { title: '積分榜', sub: '' }
  if (kind === 'top_cut') return { title: 'Top Cut', sub: '' }
  if (kind === 'final') return { title: '最終名次', sub: '' }
  return { title: kind, sub: '' }
}

export function PublicStagePage() {
  const { slug, stageId } = useParams()
  const [stage, setStage] = useState<{ id: string; kind: string; round: number | null; parsed: unknown; displayMode: string; uploads?: Array<{ id: string }> } | null>(null)
  const [activeTab, setActiveTab] = useState<'table' | 'images'>('table')
  const [error, setError] = useState<string | null>(null)
  const [event, setEvent] = useState<{ title: string; stages: Array<{ id: string; kind: string; round: number | null }> } | null>(null)
  const storageKey = slug ? `ptcg.playerFilter:${slug}` : null
  const [filter, setFilter] = useState('')
  const [onlyMatches, setOnlyMatches] = useState(true)

  useEffect(() => {
    if (!slug || !stageId) return
    void api
      .publicStage(slug, stageId)
      .then((r) => {
        setStage(r.stage)
        // Default to images if table data is empty
        if (r.stage.displayMode === 'both' && !r.stage.parsed) {
          setActiveTab('images')
        } else if (r.stage.displayMode === 'images') {
          setActiveTab('images')
        } else {
          setActiveTab('table')
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : '載入失敗'))
  }, [slug, stageId])

  useEffect(() => {
    if (!slug) return
    void api
      .publicEvent(slug)
      .then((r) => setEvent({ title: r.event.title, stages: r.stages.map((s) => ({ id: s.id, kind: s.kind, round: s.round })) }))
      .catch(() => setEvent(null))
  }, [slug])

  useEffect(() => {
    if (!storageKey) return
    try {
      const v = localStorage.getItem(storageKey)
      if (v) setFilter(v)
    } catch {
      // ignore
    }
  }, [storageKey])

  useEffect(() => {
    if (!storageKey) return
    try {
      if (filter) localStorage.setItem(storageKey, filter)
      else localStorage.removeItem(storageKey)
    } catch {
      // ignore
    }
  }, [storageKey, filter])

  // IMPORTANT: keep hooks above early returns.
  const kind = stage?.kind ?? ''
  const round = stage?.round ?? null
  const t = pageTitle(kind, round)
  const quickRounds = useMemo(() => {
    if (!event) return []
    if (kind !== 'pairings' && kind !== 'round_results') return []
    return event.stages
      .filter((s) => s.kind === kind && s.round != null)
      .sort((a, b) => (a.round ?? 0) - (b.round ?? 0))
  }, [event, kind])

  if (error) return <div className="card">無法載入：{error}</div>
  if (!stage) return <Loading />

  return (
    <div className="stack">
      <div className="public-stage-topbar">
        <Link className="btn" to={`/e/${slug}`}>
          返回輪次列表
        </Link>
        {quickRounds.length > 0 ? (
          <div className="quick-rounds">
            {quickRounds.map((s) => (
              <Link
                key={s.id}
                to={`/e/${slug}/${s.id}`}
                className={`quick-round ${s.id === stage.id ? 'active' : ''}`}
                aria-label={`第 ${s.round} 輪`}
              >
                {s.round}
              </Link>
            ))}
          </div>
        ) : null}
      </div>

      <div className="card stack">
        <div className="public-stage-header">
          <div className="public-title-row">
            <h1 style={{ margin: 0 }}>{t.title}</h1>
            {event?.title ? <div className="public-event-title">{event.title}</div> : null}
          </div>
          {t.sub ? <div className="muted">{t.sub}</div> : null}
        </div>
        {stage.displayMode === 'both' && (
          <div className="tabs-nav">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'table' ? 'active' : ''}`}
              onClick={() => setActiveTab('table')}
            >
              {stage.kind === 'player_list' ? '參加者列表' : '結果表格'}
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'images' ? 'active' : ''}`}
              onClick={() => setActiveTab('images')}
            >
              原始圖片
            </button>
          </div>
        )}

        {activeTab === 'table' && stage.displayMode !== 'images' && (
          <PlayerFilterBar
            value={filter}
            onChange={setFilter}
            onlyMatches={onlyMatches}
            onToggleOnlyMatches={() => setOnlyMatches((v) => !v)}
          />
        )}

        {(stage.displayMode === 'images' || (stage.displayMode === 'both' && activeTab === 'images')) ? (
          <div className="stack" style={{ gap: '1rem' }}>
            <div className="muted" style={{ textAlign: 'center' }}>
              {stage.displayMode === 'images' ? '（此階段設定為直接顯示圖片）' : '（查看 AI 解析前的原始截圖）'}
            </div>
            {stage.uploads && stage.uploads.length > 0 ? (
              stage.uploads.map((u) => (
                <img
                  key={u.id}
                  src={`/api/public/stages/${stage.id}/uploads/${u.id}/image`}
                  alt="Stage image"
                  className="modal-img"
                  style={{ marginTop: 0 }}
                />
              ))
            ) : (
              <div className="muted" style={{ textAlign: 'center', padding: '2rem' }}>未上傳圖片</div>
            )}
          </div>
        ) : (
          <StageRenderer kind={stage.kind} data={stage.parsed} filter={filter} onlyMatches={onlyMatches} />
        )}
      </div>
    </div>
  )
}

function norm(s: unknown) {
  return String(s ?? '').trim().toLowerCase()
}

function matchesFilter(filter: string, ...fields: Array<unknown>) {
  const f = norm(filter)
  if (!f) return true
  return fields.some((x) => norm(x).includes(f))
}

function PlayerFilterBar({
  value,
  onChange,
  onlyMatches,
  onToggleOnlyMatches,
}: {
  value: string
  onChange: (v: string) => void
  onlyMatches: boolean
  onToggleOnlyMatches: () => void
}) {
  return (
    <div className="card filter-bar">
      <div className="stack" style={{ gap: '0.5rem' }}>
        <div style={{ fontWeight: 700 }}>快速搜尋</div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <input
            className="input"
            placeholder="輸入姓名或 Player ID"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1, maxWidth: 'none' }}
          />
          <button className="btn btn-sm" type="button" onClick={() => onChange('')} disabled={!value}>
            清除
          </button>
        </div>
        <label className="row muted" style={{ userSelect: 'none' }}>
          <input type="checkbox" checked={onlyMatches} onChange={onToggleOnlyMatches} />
          只顯示相關
        </label>
      </div>
    </div>
  )
}

function StageRenderer({ kind, data, filter, onlyMatches }: { kind: string; data: unknown; filter: string; onlyMatches: boolean }) {
  if (!data || typeof data !== 'object') return <div className="muted">（空）</div>

  const f = filter.trim()

  if (kind === 'player_list') {
    const d = data as {
      players?: Array<{ player_id: string; name: string }>
      updatedLabel?: string
    }
    const rows = (d.players ?? []).filter((p) => matchesFilter(f, p.player_id, p.name))
    const shown = onlyMatches && f ? rows : (d.players ?? [])
    const totalPlayers = (d.players ?? []).length
    const matchedPlayers = rows.length
    return (
      <div className="stack">
        {d.updatedLabel ? <div className="muted">{d.updatedLabel}</div> : null}
        <div className="player-list-meta muted">
          <span>符合：{matchedPlayers} / {totalPlayers}</span>
          <span>玩家數：{totalPlayers}</span>
        </div>
        <div className="table-wrap public-table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Player ID</th>
                <th>姓名</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p, i) => (
                <tr key={`${p.player_id}-${i}`} className={f && matchesFilter(f, p.player_id, p.name) ? 'row-hit' : ''}>
                  <td>{p.player_id}</td>
                  <td>{p.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (kind === 'pairings') {
    const d = data as {
      pairings?: Array<{
        table: number
        is_bye?: boolean
        player1: { id: string; name: string; record?: string; division?: string; seat_or_suffix?: string }
        player2?: { id?: string; name?: string; record?: string; division?: string; seat_or_suffix?: string }
      }>
    }
    const all = d.pairings ?? []
    const totalPlayers = all.reduce((sum, m) => sum + (m.player1.id || m.player1.name ? 1 : 0) + (m.player2?.id || m.player2?.name ? 1 : 0), 0)
    const byeCount = all.filter((m) => !!m.is_bye || !(m.player2?.id || m.player2?.name)).length
    const hits = all.filter((m) =>
      matchesFilter(f, m.player1.id, m.player1.name, m.player2?.id, m.player2?.name, m.table)
    )
    const shown = onlyMatches && f ? hits : all
    return (
      <div className="stack">
        <StageStats
          items={[
            { key: 'tables', label: '桌數', value: all.length, icon: 'table' },
            { key: 'players', label: '玩家數', value: totalPlayers, icon: 'users' },
            { key: 'bye', label: 'Bye 數', value: byeCount, icon: 'bye' },
          ]}
        />
        {f ? <div className="muted">符合：{hits.length} / {all.length}</div> : null}
        <div className="table-cards-grid">
          {shown.map((m) => {
            const hit = f ? matchesFilter(f, m.player1.id, m.player1.name, m.player2?.id, m.player2?.name, m.table) : false
            return (
              <div key={m.table} className={`card stack ${hit ? 'card-hit' : ''}`} style={{ padding: '0.85rem' }}>
                <div className="table-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.4rem', marginBottom: '0.25rem' }}>
                  <svg className="muted" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="9" y2="9"/><line x1="4" x2="20" y1="15" y2="15"/><line x1="10" x2="8" y1="3" y2="21"/><line x1="16" x2="14" y1="3" y2="21"/></svg>
                  {`Table ${m.table}`}
                </div>
                <div className="match-grid">
                  <PlayerCard p={m.player1} highlight={!!f && matchesFilter(f, m.player1.id, m.player1.name)} />
                  {m.is_bye ? (
                    <div className="muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>BYE / 輪空</div>
                  ) : m.player2?.name || m.player2?.id ? (
                    <PlayerCard p={m.player2} highlight={!!f && matchesFilter(f, m.player2?.id, m.player2?.name)} />
                  ) : (
                    <div className="muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>（無對手）</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (kind === 'round_results') {
    const d = data as {
      matches?: Array<{
        table: number
        is_bye?: boolean
        player1: { id: string; name: string; record?: string; points?: number; is_winner?: boolean }
        player2?: { id?: string; name?: string; record?: string; points?: number; is_winner?: boolean }
      }>
    }
    const all = d.matches ?? []
    const totalPlayers = all.reduce((sum, m) => sum + (m.player1.id || m.player1.name ? 1 : 0) + (m.player2?.id || m.player2?.name ? 1 : 0), 0)
    const byeCount = all.filter((m) => !!m.is_bye || !(m.player2?.id || m.player2?.name)).length
    const doubleLosses = all.filter((m) =>
      !m.is_bye &&
      (m.player2?.id || m.player2?.name) &&
      m.player1.is_winner === false &&
      m.player2?.is_winner === false
    ).length
    const hits = all.filter((m) =>
      matchesFilter(f, m.player1.id, m.player1.name, m.player2?.id, m.player2?.name, m.table)
    )
    const shown = onlyMatches && f ? hits : all
    return (
      <div className="stack">
        <StageStats
          items={[
            { key: 'tables', label: '桌數', value: all.length, icon: 'table' },
            { key: 'players', label: '玩家數', value: totalPlayers, icon: 'users' },
            { key: 'bye', label: 'Bye 數', value: byeCount, icon: 'bye' },
            { key: 'double-loss', label: '雙敗數', value: doubleLosses, icon: 'double-loss', danger: true },
          ]}
        />
        {f ? <div className="muted">符合：{hits.length} / {all.length}</div> : null}
        <div className="table-cards-grid">
          {shown.map((m) => {
            const hit = f ? matchesFilter(f, m.player1.id, m.player1.name, m.player2?.id, m.player2?.name, m.table) : false
            return (
              <div key={m.table} className={`card stack ${hit ? 'card-hit' : ''}`} style={{ padding: '0.85rem' }}>
                <div className="table-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.4rem', marginBottom: '0.25rem' }}>
                  <svg className="muted" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="9" y2="9"/><line x1="4" x2="20" y1="15" y2="15"/><line x1="10" x2="8" y1="3" y2="21"/><line x1="16" x2="14" y1="3" y2="21"/></svg>
                  {`Table ${m.table}`}
                </div>
                <div className="match-grid">
                  <PlayerCard
                    p={m.player1}
                    winner={!!m.player1.is_winner}
                    highlight={!!f && matchesFilter(f, m.player1.id, m.player1.name)}
                  />
                  {m.is_bye ? (
                    <div className="muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>BYE / 輪空</div>
                  ) : m.player2?.name || m.player2?.id ? (
                    <PlayerCard
                      p={m.player2}
                      winner={!!m.player2?.is_winner}
                      highlight={!!f && matchesFilter(f, m.player2?.id, m.player2?.name)}
                    />
                  ) : (
                    <div className="muted" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>（無對手）</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (kind === 'standings' || kind === 'top_cut' || kind === 'final') {
    const d = data as {
      standings?: Array<{
        standing: number
        name: string
        flight?: string | number
        drop_round?: number | null
        match_record: string
        match_points?: number
        opponents_win_pct?: string
        opponents_opponents_win_pct?: string
      }>
    }
    const all = d.standings ?? []
    const hits = all.filter((r) => matchesFilter(f, r.name, r.standing))
    const shown = onlyMatches && f ? hits : all
    return (
      <div className="stack">
        <div className="muted">列數：{all.length}</div>
        {f ? <div className="muted">符合：{hits.length} / {all.length}</div> : null}
        <div className="table-wrap public-table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>姓名</th>
              <th>Flight</th>
              <th>Drop</th>
              <th>戰績</th>
              <th>分</th>
              <th>OWP</th>
              <th>OOWP</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.standing}-${r.name}`} className={f && matchesFilter(f, r.name, r.standing) ? 'row-hit' : ''}>
                <td>{r.standing}</td>
                <td>{r.name}</td>
                <td>{r.flight ?? ''}</td>
                <td>{r.drop_round ?? ''}</td>
                <td>{r.match_record}</td>
                <td>{r.match_points ?? ''}</td>
                <td>{r.opponents_win_pct ?? ''}</td>
                <td>{r.opponents_opponents_win_pct ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    )
  }

  return <pre className="code" style={{ margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>
}

function PlayerCard({
  p,
  winner,
  highlight,
}: {
  p: { id?: string; name?: string; record?: string; division?: string; seat_or_suffix?: string; points?: number }
  winner?: boolean
  highlight?: boolean
}) {
  const className = `player-card stack ${winner ? 'winner' : ''} ${highlight ? 'hit' : ''}`.trim()
  return (
    <div className={className} style={{ padding: '0.65rem 0.85rem', gap: '0.2rem', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
          <svg className="muted" style={{ flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <div style={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name ?? ''}</div>
        </div>
        <div className="row" style={{ gap: '0.35rem', flexShrink: 0, marginLeft: 'auto' }}>
          {p.division ? <span className="pill" style={{ background: '#1e293b', fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}>{p.division}</span> : null}
          {p.record ? <span className="pill" style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}>{p.record}</span> : null}
          {p.points != null ? <span className="pill" style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}>{p.points} pts</span> : null}
          {winner && (
            <span style={{ color: '#eab308', display: 'flex', alignItems: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
            </span>
          )}
        </div>
      </div>
      <div className="muted" style={{ fontSize: '0.75rem', paddingLeft: '1.4rem' }}>ID: {p.id ?? ''}</div>
    </div>
  )
}

function StageStats({
  items,
}: {
  items: Array<{ key: string; label: string; value: number; icon: 'table' | 'users' | 'bye' | 'double-loss'; danger?: boolean }>
}) {
  return (
    <div className="stage-stats">
      {items.map((item) => (
        <div key={item.key} className={`stage-stat ${item.danger ? 'danger' : ''}`}>
          <span className="stage-stat-icon" aria-hidden="true">
            {item.icon === 'table' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="9" y2="9"/><line x1="4" x2="20" y1="15" y2="15"/><line x1="10" x2="8" y1="3" y2="21"/><line x1="16" x2="14" y1="3" y2="21"/></svg>
            ) : item.icon === 'users' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
            ) : item.icon === 'bye' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h18"/><path d="M7 8h10v8H7z"/><path d="M9 16v2"/><path d="M15 16v2"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6"/><path d="M15 9l-6 6"/></svg>
            )}
          </span>
          <span className="stage-stat-label">{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  )
}
